import {
	ChangeDetectionStrategy,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { CalendarMonthViewDay } from "angular-calendar";
import moment from "moment";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { debounceTime, delay, distinctUntilChanged, shareReplay, startWith, takeUntil } from "rxjs/operators";

import { CalendarEvent } from "@intocare/calendar/data";
import { CURRENT_LANGUAGE, SupportedLanguage } from "@intocare/language";

import { CalendarViewType } from "../../types";

const DEFAULT_VIEW = CalendarViewType.WEEK;
const WEEK_STARTS_ON = 1;
const VIEW_CHANGE_DEBOUNCE_TIME = 300;

@Component({
	selector: "ic-calendar-component",
	templateUrl: "./calendar.component.html",
	styleUrls: ["./calendar.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CalendarComponent implements OnInit, OnDestroy, OnChanges {
	private currentLanguage = inject<SupportedLanguage>(CURRENT_LANGUAGE);

	private destroy$ = new Subject<void>();
	private readonly viewDateState$ = new BehaviorSubject(new Date());

	@ViewChild("calendar", { static: false, read: ElementRef }) set calendarNativeElement(element: ElementRef) {
		if (!element) {
			return;
		}

		const timeMarkerElements = element.nativeElement.getElementsByClassName("cal-current-time-marker");

		if (timeMarkerElements && timeMarkerElements.length >= 1) {
			const timeMarkerElement = timeMarkerElements[0];

			if (timeMarkerElement) {
				timeMarkerElement.scrollIntoView({ block: "center", inline: "nearest" });
			}
		}
	}

	// List of calendar event items
	@Input() events: CalendarEvent[];
	@Input() viewTypeLabels: { month: string; week: string } = {
		month: "month",
		week: "week",
	};
	@Input() loading = false;
	@Input() view: CalendarViewType;

	// Triggers event when the detail is closed
	@Output() detailClosed = new EventEmitter<void>();
	// Triggers event when the previous button is clicked
	@Output() previousClicked = new EventEmitter<CalendarViewType>();
	// Triggers event when the next button is clicked
	@Output() nextClicked = new EventEmitter<CalendarViewType>();

	@Output() viewDate = new EventEmitter<{
		date: Date;
		viewType: CalendarViewType;
	}>();

	// Template for the detail
	@ContentChild("eventDetailTmpl", { static: false }) eventDetailTemplate: TemplateRef<any>;
	// Template for the day
	@ContentChild("dayDetailTmpl", { static: false }) dayDetailTemplate: TemplateRef<any>;
	// Template for the event on the calendar
	@ContentChild("calendarEventTmpl", { static: false }) calendarEventTemplate: TemplateRef<any>;

	// Template for the week-event title on the calendar
	@ContentChild("calendarWeekTitleTmpl", { static: false }) calendarWeekTitleTemplate: TemplateRef<any>;
	// Template for the week-event on the calendar
	@ContentChild("calendarWeekEventTmpl", { static: false }) calendarWeekEventTemplate: TemplateRef<any>;

	// View date state for calendar view
	viewDate$ = this.viewDateState$.asObservable().pipe(distinctUntilChanged());
	// Debounced viewDate to make sure we only render the last calender when fast clicking the next buttons.
	debounceViewDate$: Observable<Date>;
	// The locale we're using
	localeFormat: SupportedLanguage;
	// Whether or not the detail template is shown
	detailShown = false;
	// The event passed to the detail template
	detailEvent: CalendarEvent | undefined = undefined;
	// The selected day passed to the detail template
	detailDay: CalendarMonthViewDay | undefined = undefined;
	viewTypeEnum = CalendarViewType;
	viewTypes = Object.values(CalendarViewType);
	viewTypeSelector = new UntypedFormControl(DEFAULT_VIEW);
	viewType$: Observable<CalendarViewType>;
	debouncedViewType$: Observable<CalendarViewType>;
	weekStartsOn = WEEK_STARTS_ON;

	ngOnInit() {
		this.localeFormat = this.currentLanguage;
		this.viewType$ = this.viewTypeSelector.valueChanges.pipe(
			startWith(this.viewTypeSelector.value),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true }),
		);

		combineLatest([this.viewDate$, this.viewType$])
			.pipe(delay(0), takeUntil(this.destroy$))
			.subscribe(([date, viewType]) => {
				this.viewDate.emit({ date, viewType });
				this.closeDetail();
			});

		// Debouncing these purely for the view because the view blocks our own inputs etc from triggering.
		this.debouncedViewType$ = this.viewType$.pipe(debounceTime(VIEW_CHANGE_DEBOUNCE_TIME));
		this.debounceViewDate$ = this.viewDate$.pipe(debounceTime(VIEW_CHANGE_DEBOUNCE_TIME));
	}

	/**
	 * Opens the detail and passes the selected day to the detail template.
	 *
	 * @param day - The Selected calendar day.
	 */
	dayClicked(day: CalendarMonthViewDay) {
		this.detailDay = day;
		this.detailEvent = undefined;
		this.detailShown = true;
	}

	/**
	 * Opens the detail and passes the selected event to the detail template.
	 *
	 * @param event - The Selected calendar event.
	 */
	showDetail(event: CalendarEvent) {
		this.detailDay = undefined;
		this.detailEvent = event;
		this.detailShown = true;
	}

	/** Closes the detail and emits to parent. */
	closeDetail() {
		this.detailShown = false;
		this.detailEvent = undefined;
		this.detailDay = undefined;

		// Let parent know that the detail is closed
		this.detailClosed.emit();
	}

	onNextClicked() {
		this.nextClicked.emit();

		const currentViewDate = this.viewDateState$.getValue();

		let newDate;

		if (this.viewTypeSelector.value === CalendarViewType.WEEK) {
			newDate = new Date(currentViewDate.setDate(currentViewDate.getDate() + 7));
		} else {
			moment(currentViewDate).add(1, "month");

			newDate = moment(currentViewDate).add(1, "month").toDate();
		}

		this.viewDateState$.next(newDate);
	}

	onPreviousClicked() {
		this.previousClicked.emit();

		const currentViewDate = this.viewDateState$.getValue();

		let newDate;

		if (this.viewTypeSelector.value === CalendarViewType.WEEK) {
			newDate = new Date(currentViewDate.setDate(currentViewDate.getDate() - 7));
		} else {
			newDate = moment(currentViewDate).subtract(1, "month").toDate();
		}

		this.viewDateState$.next(newDate);
	}

	ngOnDestroy() {
		this.destroy$.next();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.events) {
			if (this.detailEvent) {
				const openEventDetailIndex = this.events.findIndex((event) => this.detailEvent.id === event.id);

				if (!this.events || !(openEventDetailIndex >= 0)) {
					this.closeDetail();

					return;
				}

				this.detailEvent = this.events[openEventDetailIndex];
			}
		}

		if (changes.view) {
			this.viewTypeSelector.patchValue(this.view || DEFAULT_VIEW);
		}
	}
}
